// caminho: apps/web/src/utils/permissions.js
// data e hora da modificação: 2026-03-21 23:28

import { ROLE_DEFAULT_PERMISSIONS } from "constants/permissions";

const LEGACY_PERMISSION_MAP = {
  "event.dashboard.view": ["events.view", "events.manage"],

  "event.registration.view": ["events.view", "events.manage"],
  "event.registration.create": ["events.manage"],
  "event.registration.edit": ["events.manage", "events.update"],
  "event.registration.delete": ["events.manage"],
  "event.registration.print": ["events.manage"],
  "event.registration.import": ["events.manage"],

  "event.checkin.use": [
    "events.view",
    "events.manage",
    "checkin.view",
    "checkin.execute",
  ],

  "event.report.view": [
    "events.view",
    "events.manage",
    "reports.view",
    "checkin.reports",
  ],

  "event.team.view": ["events.view", "events.manage"],
  "event.team.manage": ["events.manage"],

  "event.details.view": ["events.view", "events.manage"],
  "event.details.manage": ["events.manage"],

  "event.emailLog.view": ["events.view", "events.manage"],

  "event.emailTemplate.manage": ["events.manage"],
  "event.publicForm.manage": ["events.manage"],
  "event.totem.manage": ["events.manage"],
  "event.labels.manage": ["events.manage"],
  "event.publicPage.view": ["events.view", "events.manage"],
  "event.publicPage.manage": ["events.manage"],

  // Expositores
  "event.exhibitor.view": ["events.view", "events.manage"],
  "event.exhibitor.create": ["events.manage"],
  "event.exhibitor.edit": ["events.manage", "events.update"],
  "event.exhibitor.delete": ["events.manage"],

  "event.exhibitorUser.view": ["events.view", "events.manage"],
  "event.exhibitorUser.create": ["events.manage"],
  "event.exhibitorUser.edit": ["events.manage", "events.update"],
  "event.exhibitorUser.delete": ["events.manage"],

  "event.exhibitorImport.view": ["events.view", "events.manage"],
  "event.exhibitorImport.create": ["events.manage"],
  "event.exhibitorImport.reprocess": ["events.manage"],

  "event.exhibitorLead.view": ["events.view", "events.manage"],
  "event.exhibitorLead.export": ["events.manage"],

  "event.exhibitorUnmatched.view": ["events.view", "events.manage"],

  // Globais
  "users.view": ["users.view"],
  "users.create": ["users.create"],
  "users.edit": ["users.edit", "users.update"],
  "users.update": ["users.edit", "users.update"],
  "users.delete": ["users.delete"],
  "users.permissions": ["users.permissions"],

  "events.view": ["events.view"],
  "events.create": ["events.create"],
  "events.edit": ["events.edit", "events.update"],
  "events.update": ["events.edit", "events.update"],
  "events.delete": ["events.delete"],
  "events.manage": ["events.manage"],

  "communication.view": ["communication.view"],
  "communication.manage": ["communication.manage"],

  "integrations.view": ["integrations.view"],
  "integrations.manage": ["integrations.manage"],
};

export function getAuthUser() {
  try {
    const raw = localStorage.getItem("AUTH_USER");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getUserRole() {
  const user = getAuthUser();
  return user?.role || null;
}

export function getRawUserPermissions() {
  const user = getAuthUser();
  return Array.isArray(user?.permissions) ? user.permissions : [];
}

export function getUserPermissions() {
  const user = getAuthUser();

  if (Array.isArray(user?.permissions) && user.permissions.length > 0) {
    return user.permissions;
  }

  const role = user?.role;
  if (role && ROLE_DEFAULT_PERMISSIONS[role]) {
    return ROLE_DEFAULT_PERMISSIONS[role];
  }

  return [];
}

export function expandLegacyPermissions(permissions = []) {
  const normalized = new Set(
    (Array.isArray(permissions) ? permissions : []).filter(Boolean),
  );

  if (normalized.has("*")) {
    return ["*"];
  }

  Object.entries(LEGACY_PERMISSION_MAP).forEach(
    ([modernPermission, legacyList]) => {
      const matchedLegacy = legacyList.some((legacyPermission) =>
        normalized.has(legacyPermission),
      );

      if (matchedLegacy) {
        normalized.add(modernPermission);
      }
    },
  );

  return Array.from(normalized);
}

export function getResolvedUserPermissions() {
  return expandLegacyPermissions(getUserPermissions());
}

export function getEventLink(eventId) {
  if (!eventId) return null;

  const normalizedEventId = String(eventId);
  const user = getAuthUser();
  const events = Array.isArray(user?.events) ? user.events : [];

  return (
    events.find(
      (item) =>
        String(item?.eventId) === normalizedEventId &&
        item?.isActive !== false,
    ) || null
  );
}

export function getRawEventPermissions(eventId) {
  const link = getEventLink(eventId);
  return Array.isArray(link?.permissions) ? link.permissions : [];
}

export function getResolvedEventPermissions(eventId) {
  return expandLegacyPermissions(getRawEventPermissions(eventId));
}

export function hasPermission(permission) {
  if (!permission) return true;

  const permissions = getResolvedUserPermissions();

  if (permissions.includes("*")) return true;
  return permissions.includes(permission);
}

export function hasAnyPermission(permissionList = []) {
  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return true;
  }

  const permissions = getResolvedUserPermissions();

  if (permissions.includes("*")) return true;
  return permissionList.some((permission) => permissions.includes(permission));
}

export function hasAllPermissions(permissionList = []) {
  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return true;
  }

  const permissions = getResolvedUserPermissions();

  if (permissions.includes("*")) return true;
  return permissionList.every((permission) => permissions.includes(permission));
}

export function hasEventPermission(eventId, permission) {
  if (!permission) return true;

  const globalPermissions = getResolvedUserPermissions();
  if (globalPermissions.includes("*")) return true;

  // fallback global explícito
  if (globalPermissions.includes(permission)) return true;

  const eventPermissions = getResolvedEventPermissions(eventId);
  if (eventPermissions.includes("*")) return true;

  return eventPermissions.includes(permission);
}

export function hasAnyEventPermission(eventId, permissionList = []) {
  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return true;
  }

  const globalPermissions = getResolvedUserPermissions();
  if (globalPermissions.includes("*")) return true;

  if (
    permissionList.some((permission) => globalPermissions.includes(permission))
  ) {
    return true;
  }

  const eventPermissions = getResolvedEventPermissions(eventId);
  if (eventPermissions.includes("*")) return true;

  return permissionList.some((permission) =>
    eventPermissions.includes(permission),
  );
}

export function hasAllEventPermissions(eventId, permissionList = []) {
  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return true;
  }

  const globalPermissions = getResolvedUserPermissions();
  if (globalPermissions.includes("*")) return true;

  if (
    permissionList.every((permission) => globalPermissions.includes(permission))
  ) {
    return true;
  }

  const eventPermissions = getResolvedEventPermissions(eventId);
  if (eventPermissions.includes("*")) return true;

  return permissionList.every((permission) =>
    eventPermissions.includes(permission),
  );
}